"use client";
import { Suspense, useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Stack,
  Box,
  IconButton,
  Typography,
  Divider,
  CardHeader,
  Collapse
} from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlineRounded as InfoIcon,
  CheckCircleOutlineRounded as FreeTierIcon
} from "@mui/icons-material";
import UpgradeIcon from "@mui-symbols-material/w400/DiamondRounded";
import { ExperimentRounded as ExperimentIcon } from "@mui-symbols-material/w400";

import { useStore, observer } from "../../../service/mobx";
import Header from "../../model/Header";
import useIO from "../../model/Content/Play/InputBox/useIO";
import InputBox from "../../model/Content/Play/InputBox";
import OutputBox from "../../model/Content/Play/OutputBox";
import { useModelListeners } from "../../model/Content";
import { ShallowLink } from "../../../component/Link";
import Skeleton from "../../../component/Skeleton";
import ButtonFilled from "../../../component/Button/Filled";
import ButtonText from "../../../component/Button/Text";
import ModelCode from "../../../component/Code/ModelCode";

const Docker = dynamic(() => import("./Docker"));

function Preview({ freeCount, transition }) {
  const { device } = useStore();

  useBottomSheet(freeCount, transition);

  return device.measured && device.isNotPhone ? (
    <Model freeCount={freeCount} transition={transition} />
  ) : null;
}

export default observer(Preview);

const Model = observer(function Model({ freeCount, transition }) {
  const { device, model, user } = useStore();
  const searchParams = useSearchParams();
  const modelId = searchParams.get("modelId");
  const hasModel = modelId !== null;
  const loaded = model.node?.id !== undefined;

  useEffect(() => {
    if (modelId) {
      fetch(`/api/model/${modelId}`).then(async res => {
        const modelNode = await res.json();

        model.set.node(modelNode);
      });
    }

    const previewElement = document.getElementById("preview");

    if (previewElement) {
      previewElement.style.flex = device.isNotPhone && modelId ? 3 : 0;
    }

    return model.reset;
  }, [model, modelId, user.uid, device.isNotPhone]);

  return (
    <Stack
      spacing={4}
      sx={{
        p: hasModel ? { compact: 0, expanded: 2 } : undefined,
        pl: hasModel ? 3 : undefined,
        height: "100%",
        overflow: "hidden",
        opacity: hasModel ? 1 : 0,
        transition,
        transitionDelay: "100ms",
        borderRadius: { expanded: model ? "var(--shape-lg-end)" : undefined },
        bgcolor: {
          compact: "transparent",
          expanded: "var(--surface-container-lowest)"
        }
      }}
    >
      <ModelHeader loaded={loaded} />
      <Demo loaded={loaded} />
      <Divider />
      <CodeSection loaded={loaded} freeCount={freeCount} />
    </Stack>
  );
});

function useBottomSheet(freeCount, transition) {
  const { bottomSheet, device } = useStore();
  const router = useRouter();
  const searchParams = useSearchParams();
  const modelId = searchParams.get("modelId");

  useEffect(() => {
    if (device.isPhone && modelId !== null) {
      bottomSheet.set.Component(() => (
        <Model freeCount={freeCount} transition={transition} />
      ));
      bottomSheet.set.open(true);
      bottomSheet.set.onClose(() => {
        // We protect this, if the BottomSheet is unmounted because of a navigation change
        // this will be called. The route itself will have changed by the time this is reached.
        // Which will result in the route being erroneously set back to  /models when you're on e.g. /api
        if (window.location.pathname === "/models") {
          const newSearchParams = new URLSearchParams(searchParams);

          newSearchParams.delete("modelId");

          router.push(
            `/models${
              newSearchParams.size === 0 ? "" : `?${newSearchParams.toString()}`
            }`
          );
          bottomSheet.reset();
        }
      });

      return bottomSheet.reset;
    }
  }, [
    bottomSheet,
    device.isPhone,
    freeCount,
    transition,
    modelId,
    router,
    searchParams
  ]);
}

const ModelHeader = observer(function ModelHeader({ loaded }) {
  const { device, model } = useStore();
  const newSearchParams = new URLSearchParams(useSearchParams());

  newSearchParams.delete("modelId");

  return (
    <Stack direction="row" whiteSpace="nowrap" justifyContent="space-between">
      {loaded ? (
        <Header model={model.node} size="medium" />
      ) : (
        <Stack spacing={2} height={92}>
          <Stack direction="row" spacing={2}>
            <Skeleton height={40} width={40} borderRadius="xs-round" />
            <Skeleton height={40} width={320} borderRadius="xs-round" />
          </Stack>
          <Stack direction="row" spacing={1}>
            {new Array(4).fill().map((_, index) => (
              <Skeleton
                key={index}
                height={28}
                width={90}
                borderRadius="xs-round"
              />
            ))}
          </Stack>
        </Stack>
      )}
      {device.isPhone ? null : (
        <div>
          <IconButton
            LinkComponent={ShallowLink}
            size="small"
            href={`/models${
              newSearchParams.size === 0 ? "" : `?${newSearchParams.toString()}`
            }`}
          >
            <CloseIcon
              sx={{ width: 16, height: 16, color: "var(--surface-on-variant)" }}
            />
          </IconButton>
        </div>
      )}
    </Stack>
  );
});

const Demo = observer(function Demo({ loaded }) {
  const { model } = useStore();
  const { label, labelOutput, Input, Output, Examples, notLoaded } = useIO();
  const cluster = useModelListeners();

  useEffect(() => {
    model.set.disabled(model.tier.enterprise);
  }, [model, model.allowed, model.tier.enterprise]);

  return loaded && notLoaded === undefined ? (
    <Stack spacing={1}>
      <Suspense fallback={<Skeleton height={258} />}>
        <InputBox
          small
          cluster={cluster}
          Input={Input}
          label={label}
          Examples={Examples}
        />
      </Suspense>
      {Output ? (
        <Suspense fallback={<Skeleton height={258} />}>
          <OutputBox
            small
            pricing={false}
            label={labelOutput}
            Output={Output}
          />
        </Suspense>
      ) : null}
    </Stack>
  ) : (
    <Stack spacing={1}>
      <Skeleton height={258} />
      {loaded && model.node.task !== "chat" ? <Skeleton height={258} /> : null}
    </Stack>
  );
});

const CodeSection = observer(function CodeSection({ loaded, freeCount }) {
  const { device, model, user } = useStore();

  return (
    <Stack
      spacing={2}
      p={{ compact: 0, expanded: 2 }}
      bgcolor="var(--surface-container-lowest)"
    >
      {loaded ? (
        <>
          <Typography variant="titleLg" fontWeight={500}>
            API
          </Typography>
          {user.loggedIn && model.node.source === "big-daddy" ? (
            <Experimental />
          ) : null}
          <Alert freeCount={freeCount} />
          <Suspense>
            <ModelCode />
          </Suspense>
          {model.node.modelId &&
          model.node.closedSource === false &&
          model.node.containerized !== false ? (
            <Suspense>
              <Divider variant="middle">
                <Typography
                  variant="titleMd"
                  color="var(--secondary-color)"
                  sx={{ opacity: 0.6 }}
                >
                  or
                </Typography>
              </Divider>
              <Docker model={model.node} />
            </Suspense>
          ) : null}
          <Stack
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            direction={{ compact: "column", expanded: "row" }}
          >
            <ButtonText
              fullWidth={device.isPhone}
              label="Browse Related Models"
              href={`/model/${model.node.name}/related`}
              sx={{
                borderRadius: "var(--shape-sm)",
                typography: { compact: "labelLg", expanded: "labelMd" }
              }}
            />
            {model.node.closedSource ? null : (
              <ButtonText
                fullWidth={device.isPhone}
                label="View Model Readme"
                href={`/model/${model.node.name}/details`}
                sx={{
                  borderRadius: "var(--shape-sm)",
                  typography: { compact: "labelLg", expanded: "labelMd" }
                }}
              />
            )}
            <ButtonText
              target="_blank"
              fullWidth={device.isPhone}
              label={`View ${model.node.task} Docs`}
              href={`https://docs.bytez.com/model-api/docs/task/${model.node.task}`}
              sx={{
                textTransform: "capitalize",
                borderRadius: "var(--shape-sm)",
                bgcolor: "var(--secondary-container)",
                color: "var(--secondary-on-container)",
                typography: { compact: "labelLg", expanded: "labelMd" }
              }}
            />
          </Stack>
        </>
      ) : (
        <>
          <Skeleton height={28} borderRadius="xs-round" />
          <Skeleton height={52} borderRadius="xs-round" />
          <Skeleton height={414} borderRadius="md-round" />
        </>
      )}
    </Stack>
  );
});

const Alert = observer(function Alert({ freeCount }) {
  const { device, model } = useStore();
  const { notOnApiPlan, needsUpgrade } = model;
  const state = notOnApiPlan
    ? model.tier.free
      ? "Free Tier" // good
      : "Join the community" // good
    : model.tier.premium // otherwise they're on a plan+need-upgrade
      ? "Upgrade to Access" //
      : "Invite Only";
  const green = state === "Free Tier" || state === "Join the community";
  const Icon = green
    ? FreeTierIcon
    : state === "Invite Only"
      ? InfoIcon
      : UpgradeIcon;

  /*
    not signed in || not on plan =>
      free model => "Free Tier"
      paid model || enterprise => join community
    on free tier
      free model => you have access
      paid model => upgrade
      enterprise => contact us to sign up
    on premium =>
      free or paid => you have access
      enterprise => contact us to sign up
  */

  return (
    <Collapse mountOnEnter unmountOnExit in={needsUpgrade}>
      <Box
        sx={{
          borderRadius: "var(--shape-md-round)",
          bgcolor:
            notOnApiPlan || needsUpgrade === false
              ? "#DDF8D8"
              : "var(--colors-warning-95)"
        }}
      >
        <CardHeader
          avatar={
            <Icon
              sx={{
                width: notOnApiPlan ? 32 : 40,
                height: "inherit",
                color: green
                  ? "var(--colors-success-40)"
                  : "var(--colors-warning-30)"
              }}
            />
          }
          title={state}
          subheader={
            state === "Free Tier" ? (
              <>
                Run models like{" "}
                <Box component="strong" color="var(--colors-success-40)">
                  {model.node.displayName}
                </Box>{" "}
                at no cost
              </>
            ) : state === "Join the community" ? (
              `Membership is free and you get access ${freeCount} models at no cost`
            ) : state === "Upgrade to Access" ? (
              <>
                This model requires <strong>Pay-as-you-go</strong> and isn't
                included in the Free Tier
              </>
            ) : (
              "This model requires a special plan. Join the waitlist to be considered"
            )
          }
          action={
            device.isPhone ? null : (
              <ButtonFilled
                href={
                  state === "Invite Only"
                    ? "https://forms.gle/4bVR6CKzve6FAJev6"
                    : `/api/pricing?returnTo=${encodeURIComponent(
                        window.location.href
                      )}`
                }
                target={state === "Invite Only" ? "_blank" : undefined}
                fullWidth={device.isPhone}
                label={
                  state === "Free Tier" || state === "Join the community"
                    ? "Join Free"
                    : state === "Upgrade to Access"
                      ? "Upgrade"
                      : "Join Waitlist"
                }
                sx={{
                  textAlign: "center",
                  boxShadow: "var(--elevation-1)",
                  borderRadius: "var(--shape-sm)",
                  typography: "labelLg",
                  color: green ? "#F2FCEF" : "var(--colors-warning-95)",
                  bgcolor: green
                    ? "var(--colors-success-40)"
                    : "var(--dark-surface-color)"
                }}
              />
            )
          }
          slotProps={{
            title: {
              variant: "titleMd",
              fontWeight: 600,
              color: green
                ? "var(--colors-success-40)"
                : "var(--colors-warning-30)"
            },
            subheader: {
              variant: "bodyMd",
              color: green
                ? "var(--colors-success-40)"
                : "var(--colors-warning-30)",
              sx: { textWrap: "balance" }
            }
          }}
          sx={{
            "& .MuiCardHeader-action": {
              alignSelf: "center", // vertical centering within the header row
              display: "flex",
              alignItems: "center",
              marginTop: 0, // remove default negative margins
              marginBottom: 0,
              pr: 1
            }
          }}
        />
        {device.isPhone ? (
          <ButtonFilled
            href={
              state === "Invite Only"
                ? "https://forms.gle/4bVR6CKzve6FAJev6"
                : `/api/pricing?returnTo=${encodeURIComponent(
                    window.location.href
                  )}`
            }
            target={state === "Invite Only" ? "_blank" : undefined}
            fullWidth={device.isPhone}
            label={
              state === "Free Tier" || state === "Join the community"
                ? "Join Free"
                : state === "Upgrade to Access"
                  ? "Upgrade"
                  : "Join Waitlist"
            }
            sx={{
              textAlign: "center",
              boxShadow: "var(--elevation-1)",
              borderRadius: "var(--shape-sm)",
              typography: "labelLg",
              color: green ? "#F2FCEF" : "var(--colors-warning-95)",
              bgcolor: green
                ? "var(--colors-success-40)"
                : "var(--dark-surface-color)"
            }}
          />
        ) : null}
      </Box>
    </Collapse>
  );
});
const Experimental = () => (
  <CardHeader
    avatar={
      <ExperimentIcon
        sx={{ width: 32, height: "inherit", color: "var(--colors-info-40)" }}
      />
    }
    title="Preview"
    subheader="We're testing this model. Availability not guaranteed."
    slotProps={{
      title: {
        variant: "titleMd",
        fontWeight: 600,
        color: "var(--colors-info-30)"
      },
      subheader: {
        variant: "bodyMd",
        color: "var(--colors-info-30)",
        sx: { textWrap: "balance" }
      }
    }}
    sx={{
      bgcolor: "var(--colors-info-95)",
      borderRadius: "var(--shape-md-round)",
      "& .MuiCardHeader-action": {
        alignSelf: "center", // vertical centering within the header row
        display: "flex",
        alignItems: "center",
        marginTop: 0, // remove default negative margins
        marginBottom: 0,
        pr: 1
      }
    }}
  />
);
